import React, { createContext, useContext, useState, useCallback, useRef, useMemo } from "react";

const PolicySelectionContext = createContext({
  selectedFolderPolicyIds: [],
  setSelectedFolderPolicyIds: () => {},
  selectedPolicyIds: [],
  setSelectedPolicyIds: () => {},
  selectedPolicyMeta: {
    hasAnyDeletable: false,
    hasAnyRenotifiable: false,
    hasAnyRenewable: false,
  },
  setSelectedPolicyMeta: () => {},
  archiveHandler: null,
  isArchiveTab: false,
  setIsArchiveTab: () => {},
  registerArchiveHandler: () => {},
  registerClearSelection: () => {},
  clearSelection: () => {},
  showDeleteModal: false,
  pendingDeleteIds: [],
  openDeleteModal: () => {},
  closeDeleteModal: () => {},
  registerDeleteHandler: () => {},
  deleteHandler: () => {},
  renotifyHandler: () => {},
  registerRenotifyHandler: () => {},
  exportHandler: null,
  registerExportHandler: () => {},
});

export const PolicySelectionProvider = ({ children }) => {
  const [selectedFolderPolicyIds, setSelectedFolderPolicyIds] = useState([]);
  const [selectedPolicyIds, setSelectedPolicyIds] = useState([]);
  const [selectedPolicyMeta, setSelectedPolicyMeta] = useState({
    hasAnyDeletable: false,
    hasAnyRenotifiable: false,
    hasAnyRenewable: false,
  });
  const [isArchiveTab, setIsArchiveTab] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
  const clearSelectionRef = useRef(null);
  const archiveHandlerRef = useRef(null);
  const deleteHandlerRef = useRef(null);
  const renotifyHandlerRef = useRef(null);
  const exportHandlerRef = useRef(null);

  const registerArchiveHandler = useCallback((handler) => {
    archiveHandlerRef.current = handler ?? null;
  }, []);

  const registerClearSelection = useCallback((fn) => {
    clearSelectionRef.current = fn;
  }, []);

  const registerDeleteHandler = useCallback((handler) => {
    deleteHandlerRef.current = handler ?? null;
  }, []);

  const registerRenotifyHandler = useCallback((handler) => {
    renotifyHandlerRef.current = handler ?? null;
  }, []);

  const clearSelection = useCallback(() => {
    if (clearSelectionRef.current) clearSelectionRef.current();
    setSelectedFolderPolicyIds([]);
    setSelectedPolicyIds([]);
  }, []);

  const archiveHandler = useCallback((ids) => {
    if (typeof archiveHandlerRef.current === "function") {
      archiveHandlerRef.current(ids);
    }
  }, []);

  const openDeleteModal = useCallback((ids) => {
    setPendingDeleteIds(Array.isArray(ids) ? ids : []);
    setShowDeleteModal(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
    setPendingDeleteIds([]);
  }, []);

  const deleteHandler = useCallback(
    async (ids) => {
      if (typeof deleteHandlerRef.current === "function") {
        await deleteHandlerRef.current(ids);
      }
      closeDeleteModal();
      clearSelection();
    },
    [closeDeleteModal, clearSelection],
  );

  const renotifyHandler = useCallback((ids) => {
    if (typeof renotifyHandlerRef.current === "function") {
      renotifyHandlerRef.current(ids);
    }
  }, []);

  const registerExportHandler = useCallback((handler) => {
    exportHandlerRef.current = handler ?? null;
  }, []);

  const exportHandler = useCallback(async () => {
    if (typeof exportHandlerRef.current === "function") {
      await exportHandlerRef.current();
    }
  }, []);

  const value = useMemo(
    () => ({
      selectedFolderPolicyIds,
      setSelectedFolderPolicyIds,
      selectedPolicyIds,
      setSelectedPolicyIds,
      selectedPolicyMeta,
      setSelectedPolicyMeta,
      archiveHandler,
      isArchiveTab,
      setIsArchiveTab,
      registerArchiveHandler,
      registerClearSelection,
      clearSelection,
      showDeleteModal,
      pendingDeleteIds,
      openDeleteModal,
      closeDeleteModal,
      registerDeleteHandler,
      deleteHandler,
      renotifyHandler,
      registerRenotifyHandler,
      exportHandler,
      registerExportHandler,
    }),
    [
      selectedFolderPolicyIds,
      selectedPolicyIds,
      selectedPolicyMeta,
      archiveHandler,
      isArchiveTab,
      registerArchiveHandler,
      registerClearSelection,
      clearSelection,
      showDeleteModal,
      pendingDeleteIds,
      openDeleteModal,
      closeDeleteModal,
      registerDeleteHandler,
      deleteHandler,
      renotifyHandler,
      registerRenotifyHandler,
      exportHandler,
      registerExportHandler,
    ],
  );

  return (
    <PolicySelectionContext.Provider value={value}>
      {children}
    </PolicySelectionContext.Provider>
  );
};

export const usePolicySelection = () => useContext(PolicySelectionContext);
